import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from '../../../platform/apiClient';
import { BACKEND_FEATURES } from '../../../platform/backendFeatures';
import type { InputKind, Questionnaire, Section } from '../types';
import { v4 as uuid } from 'uuid';

interface QuestionnaireEnvelope {
  versionId: string;
  versionNumber: number;
  schema: Questionnaire;
}

export interface QuestionnaireSummary {
  id: string;
  slug: string;
  jurisdiction: string;
  term: string;
  title: string;
  isActive: boolean;
  currentVersionNumber: number;
  updatedAt: string;
}

interface QuestionnaireSummaryPage {
  items: QuestionnaireSummary[];
  nextCursor?: string;
}

export interface CourtType {
  id: string;
  code: string;
  label: string;
  description?: string;
  isActive: boolean;
}

interface CourtTypeListResponse {
  items: CourtType[];
}

// Mirrors JurorApi.Shared.Questionnaire.QuestionTypeDto - the API resolves this from
// Dictionary.tblQuestionnaireCustomQuestionType directly (see QuestionTypeLookup), not from a
// hardcoded list. legacyTypeId (stringified) IS the question's wire "type" - there is no separate
// name/slug. inputKind/needsOptions/supportsFollowUps are the one piece of server-computed shape
// metadata every consumer looks up here instead of re-deriving from a hardcoded type list.
export interface QuestionTypeInfo {
  legacyTypeId: number;
  legacyDescription: string;
  inputKind: InputKind;
  needsOptions: boolean;
  supportsFollowUps: boolean;
  hardCoded: boolean;
}

// QuestionTypeGrid renders directly off this list (one tile per row) rather than a hardcoded
// frontend array, so it reflects Dictionary.tblQuestionnaireCustomQuestionType as configured.
// inputKind 'file' never appears - no legacy row maps to it (see QuestionTypeLookup).
export const useQuestionTypes = () =>
  useQuery({
    queryKey: ['question-types'] as const,
    queryFn: () => apiClient.get<QuestionTypeInfo[]>('/question-types'),
    staleTime: 5 * 60 * 1000,
  });

export interface QuestionnaireAssignment {
  id: string;
  courtTypeId: string;
  courtTypeCode: string;
  courtTypeLabel: string;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
}

// useQuestionnairesList drives the AppHeader picker. The includeDisabled
// flag flips the API's default filter — disabled rows still exist in the
// DB and re-enable from the same picker once they're visible.
export const useQuestionnairesList = (includeDisabled: boolean) =>
  useQuery({
    queryKey: ['questionnaires', { includeDisabled }] as const,
    queryFn: async () => {
      const qs = includeDisabled ? '?includeDisabled=true' : '';
      const data = await apiClient.get<QuestionnaireSummaryPage>(
        `/questionnaires${qs}`,
      );
      return data.items;
    },
  });

export interface Tenant {
  id: string;
  code: string;
  name: string;
  jurisdiction: string;
  state: string;
  isActive: boolean;
}

interface TenantListResponse {
  items: Tenant[];
}

// useTenants drives the top-bar tenant picker. The fetch deliberately
// bypasses the X-Tenant-Id header (the index is cross-tenant by design)
// — apiClient sends the header anyway, the server ignores it on this
// route, no harm done.
//
// JurorQuestionnaire.Api doesn't implement GET /tenants yet (see
// backendFeatures.ts) - disabled so this never fires a doomed request;
// components must check BACKEND_FEATURES.tenants before rendering
// tenant-picker UI rather than relying on this query erroring.
export const useTenants = () =>
  useQuery({
    queryKey: ['tenants'] as const,
    enabled: BACKEND_FEATURES.tenants,
    queryFn: async () => {
      const data = await apiClient.get<TenantListResponse>('/tenants');
      return data.items;
    },
  });

// JurorQuestionnaire.Api doesn't implement GET /court-types yet - see
// useTenants above for why this is gated the same way.
export const useCourtTypes = () =>
  useQuery({
    queryKey: ['court-types'] as const,
    enabled: BACKEND_FEATURES.courtTypeAssignment,
    queryFn: async () => {
      const data = await apiClient.get<CourtTypeListResponse>('/court-types');
      return data.items;
    },
  });

// useAssignment returns the current court-type link or undefined when no
// active assignment exists (the API replies 404, which we swallow into
// undefined to keep the UI calling code simple).
//
// JurorQuestionnaire.Api doesn't implement this endpoint yet either - gated
// off the same way as useTenants/useCourtTypes above.
export const useAssignment = (slug: string | undefined) =>
  useQuery({
    queryKey: ['questionnaire-assignment', slug] as const,
    enabled: !!slug && BACKEND_FEATURES.courtTypeAssignment,
    queryFn: async () => {
      if (!slug) return null;
      try {
        return await apiClient.get<QuestionnaireAssignment>(
          `/questionnaires/${slug}/assignment`,
        );
      } catch (e) {
        // 404 = no link yet. Anything else is real — re-throw.
        if (
          typeof e === 'object' &&
          e !== null &&
          'status' in e &&
          (e as { status: number }).status === 404
        ) {
          return null;
        }
        throw e;
      }
    },
  });

export const useSetAssignment = (slug: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (courtTypeId: string) =>
      apiClient.put<QuestionnaireAssignment>(
        `/questionnaires/${slug}/assignment`,
        { courtTypeId },
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(['questionnaire-assignment', slug], data);
    },
  });
};

export const useSetActive = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, isActive }: { slug: string; isActive: boolean }) =>
      apiClient.patch<void>(`/questionnaires/${slug}`, { isActive }),
    onSuccess: () => {
      // Both filtered + unfiltered lists need a refresh.
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    },
  });
};

// cloneSectionsWithFreshIds deep-clones an array of sections, replacing
// every section/question/follow-up id with a new uuid. Keeping the same
// IDs across copies would be fine for storage (responses are tagged with
// questionnaire_version_id so question_keys are version-scoped), but
// regenerating makes the copy feel like an independent draft and avoids
// confusing cross-questionnaire analytics down the road.
const cloneSectionsWithFreshIds = (sections: Section[]): Section[] => {
  const cloneQuestion = (q: Section['questions'][number]): Section['questions'][number] => {
    const out: Section['questions'][number] = { ...q, id: uuid() };
    if (q.followUps) {
      const fu: NonNullable<Section['questions'][number]['followUps']> = {};
      if (q.followUps.yes) fu.yes = q.followUps.yes.map(cloneQuestion);
      if (q.followUps.no) fu.no = q.followUps.no.map(cloneQuestion);
      out.followUps = fu;
    }
    return out;
  };
  return sections.map((s) => ({
    ...s,
    id: uuid(),
    questions: s.questions.map(cloneQuestion),
  }));
};

// useCreateQuestionnaire issues a PUT with the freshly-built Questionnaire.
//
// JurorQuestionnaire.Api does NOT upsert on a client-chosen slug the way the
// mock API does (see backendFeatures.ts: clientChosenSlugOnCreate). It only
// honors input.slug as an update target when that id already exists;
// otherwise it creates a new questionnaire under its own server-assigned
// numeric id and returns that id in the response envelope. This hook returns
// THAT id, not input.slug - callers (NewQuestionnaireDialog's onCreated) must
// navigate using the returned value, never the value they requested.
//
// When a courtTypeId is supplied the link is set in a separate call right
// after, using the real created id. When a sourceSlug is supplied, the
// source's sections are deep-cloned with new ids so the new questionnaire
// starts as a working copy.
export const useCreateQuestionnaire = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slug: string;
      jurisdiction: string;
      term: string;
      title: string;
      intro: string;
      courtTypeId?: string;
      sourceSlug?: string;
    }) => {
      let sections: Section[] = [];
      if (input.sourceSlug) {
        const envelope = await apiClient.get<QuestionnaireEnvelope>(
          `/questionnaires/${input.sourceSlug}`,
        );
        sections = cloneSectionsWithFreshIds(envelope.schema.sections ?? []);
      }
      const questionnaire: Questionnaire = {
        id: input.slug,
        jurisdiction: input.jurisdiction,
        term: input.term,
        title: input.title,
        intro: input.intro,
        sections,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const envelope = await apiClient.put<QuestionnaireEnvelope>(
        `/questionnaires/${input.slug}`,
        questionnaire,
      );
      const createdId = envelope.schema.id;
      // Guarded by the feature flag, not just by whether courtTypeId is set -
      // the /assignment route doesn't exist on the backend yet, and letting
      // this PUT run would throw and fail the whole create even though the
      // questionnaire itself was already saved successfully above.
      if (input.courtTypeId && BACKEND_FEATURES.courtTypeAssignment) {
        await apiClient.put(`/questionnaires/${createdId}/assignment`, {
          courtTypeId: input.courtTypeId,
        });
      }
      return createdId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questionnaires'] });
    },
  });
};
